import { RefObject, useEffect, useMemo, useRef } from "react";
import Popover from "../../../internal/components/Popover";
import { PickerList, PickerListRef } from "./PickerList";
import { getMonthNames, getDaysInMonth, getInvalidMonthsForDay } from "./utils";
import { Flex } from "../../Flex";
import { YearlessDateMode } from "..";
import dateFieldYearlessStyles from "../DateFieldYearless.module.scss";
import type { PopoverTriggerProps } from "../../../internal/components/Popover/PopoverTrigger";

/**
 * Props for the YearlessDateSelectionPopover component
 */
export type YearlessDateSelectionPopoverProps = {
  popoverRef: RefObject<{
    openPopover: () => void;
    closePopover: () => void;
  }>;
  popoverContentRef: RefObject<HTMLDivElement>;
  month: number | null;
  day: number | null;
  onMonthChange: (month: number | null) => void;
  onDayChange: (day: number | null) => void;
  onClose: () => void;
  mode: YearlessDateMode;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
} & PopoverTriggerProps;

/**
 * A yearless date selection tool built into a popover.
 * @param param0
 * @returns
 */
export function YearlessDateSelectionPopover({
  children,
  popoverRef,
  popoverContentRef,
  month,
  day,
  onMonthChange,
  onDayChange,
  onClose,
  mode,
  onFocus,
  onBlur,
}: YearlessDateSelectionPopoverProps) {
  const monthListboxRef = useRef<PickerListRef>(null);
  const dayListboxRef = useRef<PickerListRef>(null);

  const months = useMemo(() => {
    return getMonthNames({ locale: "en-US", format: "short" }).map(
      (name, index) => ({
        label: name,
        value: index + 1,
      }),
    );
  }, []);

  const days = useMemo(() => {
    return Array.from({ length: 31 }, (_, i) => ({
      label: `${i + 1}`,
      value: i + 1,
    }));
  }, []);

  const invalidDays = useMemo(() => {
    if (!month) {
      return [];
    }
    const daysInMonth = getDaysInMonth(month);
    return Array.from(
      { length: 31 - daysInMonth },
      (_, i) => daysInMonth + i + 1,
    );
  }, [month]);

  const invalidMonths = useMemo(() => {
    if (!day) {
      return [];
    }
    return getInvalidMonthsForDay(day);
  }, [day]);

  const scrollToSelectedOption = (
    value: number | null,
    ref: RefObject<PickerListRef>,
    behavior: ScrollBehavior = "smooth",
  ) => {
    if (value && ref.current) {
      ref.current.scrollToValue(value, behavior);
    }
  };

  useEffect(() => {
    scrollToSelectedOption(month, monthListboxRef, "smooth");
  }, [month]);

  useEffect(() => {
    scrollToSelectedOption(day, dayListboxRef, "smooth");
  }, [day]);

  const handleMonthChange = (
    selected: { label: string; value: number } | undefined,
  ) => {
    if (selected?.value) {
      onMonthChange?.(selected.value);

      // If current day is invalid for the new month, clear it
      if (day && invalidDays.includes(day)) {
        onDayChange?.(null);
      }
    }
  };

  const handleDayChange = (
    selected: { label: string; value: number } | undefined,
  ) => {
    if (selected?.value) {
      onDayChange?.(selected.value);

      // If current month is invalid for the new day, clear it
      if (month && invalidMonths.includes(month)) {
        onMonthChange?.(null);
      }
    }
  };

  const monthPicker = (
    <PickerList
      ref={monthListboxRef}
      items={months.map((item) => ({
        ...item,
        disabled: invalidMonths.includes(item.value),
      }))}
      selected={months.find((m) => m.value === month)}
      style={{ maxHeight: "240px", overflow: "auto" }}
      className={dateFieldYearlessStyles["scrolling-container"]}
      onSelectionChange={handleMonthChange}
    />
  );

  const dayPicker = (
    <PickerList
      ref={dayListboxRef}
      items={days.map((item) => ({
        ...item,
        disabled: invalidDays.includes(item.value),
      }))}
      selected={days.find((d) => d.value === day)}
      className={dateFieldYearlessStyles["scrolling-container"]}
      onSelectionChange={handleDayChange}
    />
  );

  return (
    <Popover
      ref={popoverRef}
      placement="bottom-start"
      role="listbox"
      onClose={onClose}
      onOpenAnimationStart={() => {
        scrollToSelectedOption(month, monthListboxRef, "instant");
        scrollToSelectedOption(day, dayListboxRef, "instant");
      }}
    >
      <Popover.Trigger>
        {(triggerProps) => <>{children(triggerProps)}</>}
      </Popover.Trigger>
      <Popover.Content
        ref={popoverContentRef}
        onFocus={onFocus}
        onBlur={onBlur}
        hideWhileClosed
      >
        <Flex gap={2} style={{ maxHeight: "240px" }}>
          {mode === "dd/mm" ? (
            <>
              {dayPicker}
              {monthPicker}
            </>
          ) : (
            <>
              {monthPicker}
              {dayPicker}
            </>
          )}
        </Flex>
      </Popover.Content>
    </Popover>
  );
}
